import React, { useState, useEffect } from 'react'
import { graphql, Link } from 'gatsby'
import tw, { styled } from 'twin.macro'
import capitalize from 'capitalize'

// Components
import Layout from '../components/Layout'
import {
  ButtonStyle,
  CopySMStyle,
  CopyStyle,
  HeaderStyle,
  SubheadSMStyle,
  SubheadStyle,
} from '../components/UI'
import CallToAction from '../components/CallToAction'
import ReactPaginate from 'react-paginate'
import { Listbox } from '@headlessui/react'
import SliderUnstyled, { sliderUnstyledClasses } from '@mui/base/SliderUnstyled'
import { GatsbyImage } from 'gatsby-plugin-image'
import Seo from '../components/Seo'

const Jobs = ({ location, pageContext, data }) => {
  const pageData = data.contentfulJobsPage
  const jobs = pageContext.jobs

  const locations = []
  jobs.forEach((job) => {
    const city = job[1].usa_city || job[1].canada_city
    if (!locations.includes(city) && city !== undefined && city !== '')
      locations.push(city)
  })
  locations.sort((a, b) => a.localeCompare(b))

  const [keyword, setKeyword] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('Select Location')
  const [rateSliderValue, setRateSliderValue] = useState(30)
  const [order, setOrder] = useState('newest')
  const [filterResults, setFilterResults] = useState([])
  const [pageNumber, setPageNumber] = useState(0)

  useEffect(() => {
    updateResults()
  }, [rateSliderValue, order])

  useEffect(() => {
    updateResults()
  }, [])

  const filterKeyword = (jobKeyword, keyword) => {
    if (
      jobKeyword &&
      jobKeyword.toLowerCase().includes(keyword.toLowerCase())
    ) {
      return true
    } else {
      return false
    }
  }

  const filterLocation = (jobLocation, location) => {
    return jobLocation
      ? jobLocation.toLowerCase().includes(location.toLowerCase())
      : false
  }

  const filterRate = (jobRate, rate) => {
    if (jobRate && jobRate >= rate) {
      return true
    } else {
      return false
    }
  }

  const updateResults = () => {
    // Filter by keyword, location, and rate
    const filtered = jobs.filter((job) => {
      if (
        keyword === ''
          ? true
          : filterKeyword(job[1].title, keyword) ||
            filterKeyword(job[1].description, keyword)
      ) {
        if (
          selectedLocation === 'Select Location'
            ? true
            : filterLocation(job[1].location, selectedLocation)
        ) {
          if (filterRate(job[1].salary_min, rateSliderValue)) {
            return true
          }
        }
      } else {
        return false
      }
    })

    // Sort
    if (order === 'newest') {
      filtered.sort((first, next) => {
        return new Date(next[1].date) - new Date(first[1].date)
      })
    } else if (order === 'rateHighToLow') {
      filtered.sort((first, next) => {
        return next[1].salary_min - first[1].salary_min
      })
    } else if (order === 'rateLowToHigh') {
      filtered.sort((first, next) => {
        return first[1].salary_min - next[1].salary_min
      })
    }

    // Set filtered results usestate
    setFilterResults(filtered)

    // Set pagination page
    setPageNumber(0)
  }

  return (
    <Layout location={location}>
      <Seo
        title={pageData.metadataTitle || 'Job Board'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section tw="bg-[#202C3A]">
        <div tw="flex flex-col lg:(grid grid-cols-[1fr repeat(12, calc(1024px/12)) 1fr] relative h-[369px])">
          {/* Image */}
          <div tw="order-1 h-48 relative overflow-x-hidden lg:(order-2 col-start-9 col-span-6 h-full)">
            <GatsbyImage
              objectFit="cover"
              tw="w-full lg:(h-full absolute top-0)"
              image={pageData.heroImage.gatsbyImageData}
              alt={pageData.heroImage.filename}
            />
          </div>
          {/* Content */}
          <div tw="order-2 mx-7 my-12 lg:(order-1 flex flex-col justify-center mx-0 mb-0 col-start-2 col-span-5 my-0)">
            <h1 css={[HeaderStyle, tw`mb-6 text-[#C2E1EA]`]}>
              {pageData.heroHeadline}
            </h1>
            <p css={[CopyStyle, tw`max-h-full text-[#F1FAFD]`]}>
              {pageData.heroSubhead}
            </p>
          </div>
        </div>
      </section>

      <section tw="grid grid-cols-4 mx-7 pt-14 pb-28 lg:(max-w-screen-lg grid-cols-12 mx-auto pt-16 pb-32)">
        {/* Search bars */}
        <div tw="col-span-4 mb-10 lg:(col-span-full mb-16)">
          <form
            css={[CopyStyle, tw`lg:(flex items-center)`]}
            onSubmit={(e) => {
              e.preventDefault()
              updateResults()
            }}
          >
            {/* Text input */}
            <input
              css={[
                CopyStyle,
                tw`border border-black border-b-0 w-full p-7 lg:(flex-1 border-b border-r-0)`,
              ]}
              type="text"
              id="Keyword"
              name="Keyword"
              placeholder="Keyword"
              onChange={(e) => {
                setKeyword(e.target.value)
              }}
            />
            {/* Dropdown */}
            {locations && (
              <Listbox
                value={selectedLocation}
                onChange={setSelectedLocation}
                as="div"
                tw="relative w-full lg:(w-4/12)"
              >
                {({ open }) => (
                  <>
                    <Listbox.Button tw="border border-black w-full p-7 text-left flex justify-between items-center">
                      <span>{selectedLocation}</span>
                      <svg
                        width="12"
                        height="7"
                        viewBox="0 0 12 7"
                        fill="none"
                        css={[
                          tw`text-[#FF4D00] stroke-current w-3`,
                          open && tw`rotate-180`,
                        ]}
                      >
                        <path
                          d="M1 1L6 6L11 1"
                          stroke=""
                          strokeWidth="1.04499"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Listbox.Button>
                    <Listbox.Options tw="border border-black bg-white w-full absolute top-full border-t-0 h-[500px] overflow-y-scroll">
                      <Listbox.Option
                        key={`location-all`}
                        value="Select Location"
                      >
                        {({ active, selected }) => (
                          <div
                            css={[
                              tw`px-7 py-5 cursor-pointer`,
                              active && tw`bg-[#E5EEFF]`,
                            ]}
                          >
                            Select Location
                          </div>
                        )}
                      </Listbox.Option>
                      {locations.map((location) => {
                        return (
                          <Listbox.Option
                            key={`location-${location}`}
                            value={location}
                          >
                            {({ active, selected }) => (
                              <div
                                css={[
                                  tw`px-7 py-5 cursor-pointer`,
                                  active && tw`bg-[#E5EEFF]`,
                                ]}
                              >
                                {location}
                              </div>
                            )}
                          </Listbox.Option>
                        )
                      })}
                    </Listbox.Options>
                  </>
                )}
              </Listbox>
            )}
            {/* Button */}
            <input
              css={[
                ButtonStyle,
                tw`p-7 bg-[#FF4D00] text-white border-[3px] border-[#FF4D00] w-full cursor-pointer lg:(w-2/12)`,
              ]}
              type="submit"
              value="Search"
            />
          </form>
        </div>
        {/* Filters */}
        <div tw="col-span-full mb-10 lg:(col-span-3 mb-0)">
          {/* <div css={[SubheadStyle, tw`text-[#FF4D00] mb-8`]}>Toronto</div>
          <div>
            <div css={[SubheadSMStyle, tw`mb-4`]}>Field</div>
            <button
              css={[
                CopyStyle,
                tw`block px-9 py-1.5 w-full text-left -ml-9 text-[#FF4D00] bg-[#F1FAFD]`,
              ]}
            >
              Field lorem ipsum
            </button>
            <button
              css={[CopyStyle, tw`block px-9 py-1.5 w-full text-left -ml-9`]}
            >
              Field lorem ipsum
            </button>
            <button
              css={[CopyStyle, tw`block px-9 py-1.5 w-full text-left -ml-9`]}
            >
              Field lorem ipsum
            </button>
            <button
              css={[CopyStyle, tw`block px-9 py-1.5 w-full text-left -ml-9`]}
            >
              Field lorem ipsum
            </button>
          </div>
          <hr tw="border-2 border-[#F1FAFD] my-6 lg:(mt-4 mb-7 -ml-9)" /> */}
          <div>
            <div tw="flex justify-between items-center mb-4">
              <div css={[SubheadSMStyle]}>Rate</div>
              <div css={[CopyStyle]}>{`Min $${rateSliderValue}`}</div>
            </div>
            <StyledSlider
              onChangeCommitted={(e, v) => {
                setRateSliderValue(v)
              }}
              aria-label="Rate"
              defaultValue={30}
              step={10}
              marks
              min={0}
              max={120}
            />
          </div>
          <hr tw="border-2 border-[#F1FAFD] my-6 lg:(mt-4 mb-7 -ml-9)" />
          <div>
            <div css={[SubheadSMStyle, tw`mb-4`]}>Order</div>
            <button
              css={[
                CopyStyle,
                tw`block px-9 py-1.5 w-full text-left -ml-9`,
                order === 'newest' && tw`text-[#FF4D00] bg-[#F1FAFD]`,
              ]}
              onClick={() => {
                setOrder('newest')
              }}
            >
              Newest
            </button>
            <button
              css={[
                CopyStyle,
                tw`block px-9 py-1.5 w-full text-left -ml-9`,
                order === 'rateHighToLow' && tw`text-[#FF4D00] bg-[#F1FAFD]`,
              ]}
              onClick={() => {
                setOrder('rateHighToLow')
              }}
            >
              Rate High to Low
            </button>
            <button
              css={[
                CopyStyle,
                tw`block px-9 py-1.5 w-full text-left -ml-9`,
                order === 'rateLowToHigh' && tw`text-[#FF4D00] bg-[#F1FAFD]`,
              ]}
              onClick={() => {
                setOrder('rateLowToHigh')
              }}
            >
              Rate Low to High
            </button>
          </div>
        </div>
        {/* Paginated job results */}
        <div tw="col-span-4 lg:(col-start-5 col-span-8)">
          <div css={[SubheadStyle, tw`mb-8`]}>
            <span>{keyword}</span>
            <span css={[CopyStyle, tw`inline-block ml-2`]}>{`${
              filterResults.length > 9 ? 9 : filterResults.length
            } of ${filterResults.length} results`}</span>
          </div>
          <PaginatedItems
            allJobs={filterResults}
            itemsPerPage={9}
            page={pageNumber}
            setPage={setPageNumber}
          />
        </div>
      </section>

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[
          { copy: 'Start Earning', destination: '/start-earning' },
          { copy: 'Find Talent', destination: '/find-talent' },
        ]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default Jobs

export const pageQuery = graphql`
  {
    contentfulJobsPage(pageId: { eq: "Jobs Page" }) {
      heroHeadline
      heroSubhead
      heroImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }
  }
`

function Items({ currentItems }) {
  return (
    <div>
      {currentItems &&
        currentItems.map((job, index) => (
          <Link
            key={`job-${index}`}
            to={`${job[1].country.toLowerCase()}/${(
              job[1].canada_city || job[1].usa_city
            )
              .toLowerCase()
              .replace(' ', '-')}/${job[1].id}`}
          >
            <div tw="border border-black w-full py-7 px-8 mb-4 lg:(py-8 px-12)">
              <div tw="flex justify-between">
                <h2 css={[SubheadSMStyle]}>{capitalize.words(job[1].title)}</h2>
                <span css={[CopyStyle]}>{`$${
                  job[1].salary_max
                } ${job[1].currency.toUpperCase()} / ${
                  job[1].pay_duration === 'Hourly'
                    ? 'hour'
                    : job[1].pay_duration.toLowerCase()
                }`}</span>
              </div>
              {job[1].canada_city && (
                <span
                  css={[CopySMStyle]}
                >{`${job[1].canada_city}, ${job[1].canada_province}`}</span>
              )}
              {job[1].usa_city && (
                <span
                  css={[CopySMStyle]}
                >{`${job[1].usa_city}, ${job[1].usa_state}`}</span>
              )}
            </div>
          </Link>
        ))}
    </div>
  )
}

function PaginatedItems({ allJobs, itemsPerPage, page, setPage }) {
  // We start with an empty list of items.
  const [currentItems, setCurrentItems] = useState(null)
  const [pageCount, setPageCount] = useState(0)
  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.
  const [itemOffset, setItemOffset] = useState(0)

  useEffect(() => {
    const newOffset = (page * itemsPerPage) % allJobs.length
    setItemOffset(newOffset)
    // Fetch items from another resources.
    const endOffset = itemOffset + itemsPerPage
    // console.log(`Loading items from ${itemOffset} to ${endOffset}`)
    setCurrentItems(allJobs.slice(itemOffset, endOffset))
    setPageCount(Math.ceil(allJobs.length / itemsPerPage))
  }, [itemOffset, itemsPerPage, allJobs, page])

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % allJobs.length
    // console.log(`User requested page number ${event.selected}, which is offset ${newOffset}`)
    setItemOffset(newOffset)
    setPage(event.selected)
  }

  return (
    <>
      <Items currentItems={currentItems} />
      <ReactPaginate
        forcePage={page}
        breakLabel="..."
        nextLabel=">"
        onPageChange={handlePageClick}
        pageRangeDisplayed={5}
        pageCount={pageCount}
        previousLabel="<"
        renderOnZeroPageCount={null}
        css={[
          CopyStyle,
          tw`w-full mt-10 flex justify-center items-center lg:mt-16`,
        ]}
        nextClassName="pagination-next"
        previousClassName="pagination-previous"
        activeClassName="pagination-active"
        disabledClassName="pagination-disabled"
        pageClassName="pagination-page"
      />
    </>
  )
}

const StyledSlider = styled(SliderUnstyled)(
  ({ theme }) => `
  color: #1976d2;
  height: 4px;
  width: 100%;
  padding: 13px 0;
  display: inline-block;
  position: relative;
  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 1;
  }

  &.${sliderUnstyledClasses.disabled} { 
    pointer-events: none;
    cursor: default;
    color: #bdbdbd; 
  }

  & .${sliderUnstyledClasses.rail} {
    display: block;
    position: absolute;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background-color: #FF4D00;
  }

  & .${sliderUnstyledClasses.track} {
    display: block;
    position: absolute;
    height: 4px;
    border-radius: 2px;
    background-color: #C2E1EA;
  }

  & .${sliderUnstyledClasses.thumb} {
    position: absolute;
    width: 14px;
    height: 14px;
    margin-left: -6px;
    margin-top: -5px;
    box-sizing: border-box;
    border-radius: 50%;
    outline: 0;
    border: 2px solid #FF4D00;
    background-color: #FF4D00;

    :hover,
    &.${sliderUnstyledClasses.focusVisible} {
      box-shadow: 0 0 0 0.25rem #1976d2;
    }

    &.${sliderUnstyledClasses.active} {
      box-shadow: 0 0 0 0.25rem #1976d2;
    }
  }
`
)
